import type { ExceptionFrame, ExceptionMechanism } from '@belvdr/shared'
import { parseStack } from './stack.js'

/** Longueurs maximales du message et du type (cf. `@belvdr/shared`). */
const MAX_EXCEPTION_MESSAGE_LENGTH = 10_000
const MAX_EXCEPTION_TYPE_LENGTH = 256
/** Type des valeurs jetées qui ne sont pas des `Error` (cf. `NON_ERROR_TYPE`). */
const NON_ERROR_TYPE = 'Error'

/**
 * Capture des Exceptions de la page (voir `CONTEXT.md`), indépendante de rrweb : une valeur jetée
 * en entrée, une Exception structurée (type, message, frames) en sortie. Un `console.error` n'est
 * pas une Exception : le plugin console l'enregistre dans le replay, ce module l'ignore.
 */

/** Ce que le SDK sait d'une erreur, avant d'y ajouter l'instant, l'URL et la session. */
export type ParsedException = {
  type: string
  message: string
  frames: ExceptionFrame[]
  handled: boolean
  mechanism: ExceptionMechanism
}

type ErrorLike = { name?: unknown; message: string; stack?: unknown }

function isErrorLike(value: unknown): value is ErrorLike {
  if (value instanceof Error) return true
  // Erreur d'une autre fenêtre (iframe) : pas une instance de l'`Error` de cette page.
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ErrorLike).message === 'string' &&
    typeof (value as ErrorLike).name === 'string'
  )
}

/** Préfixe ajouté par les navigateurs au message d'un `ErrorEvent`. */
const UNCAUGHT = /^Uncaught (?:\(in promise\) )?/
/** `TypeError: …` en tête d'un message : le type de l'erreur, quand elle n'est pas fournie. */
const TYPED_MESSAGE = /^([A-Z][A-Za-z0-9_$]*(?:Error|Exception)): ([\s\S]*)$/

const clip = (text: string, max: number) => text.slice(0, max)

/** Valeur jetée qui n'est pas une `Error`, sérialisée lisiblement (chaîne telle quelle, JSON). */
function serialize(value: unknown): string {
  if (typeof value === 'string') return value
  if (value === undefined || typeof value === 'function' || typeof value === 'symbol')
    return String(value)
  try {
    return JSON.stringify(value) ?? String(value)
  } catch {
    // Objet circulaire, BigInt…
    return Object.prototype.toString.call(value)
  }
}

/** Exception d'une valeur jetée, quelle qu'elle soit (une `Error`, une chaîne, un objet…). */
export function parseThrown(
  value: unknown,
  { handled, mechanism }: Pick<ParsedException, 'handled' | 'mechanism'>,
): ParsedException {
  if (isErrorLike(value)) {
    const name = typeof value.name === 'string' && value.name !== '' ? value.name : NON_ERROR_TYPE
    return {
      type: clip(name, MAX_EXCEPTION_TYPE_LENGTH),
      message: clip(value.message, MAX_EXCEPTION_MESSAGE_LENGTH),
      frames: parseStack(value.stack),
      handled,
      mechanism,
    }
  }
  return {
    type: NON_ERROR_TYPE,
    message: clip(serialize(value), MAX_EXCEPTION_MESSAGE_LENGTH),
    frames: [],
    handled,
    mechanism,
  }
}

/**
 * Exception d'un `ErrorEvent` sans erreur jointe (script d'une autre origine, ancien navigateur) :
 * son message, et la position que donne le navigateur.
 */
function parseErrorEvent(event: ErrorEvent): ParsedException {
  const message = (event.message ?? '').replace(UNCAUGHT, '')
  const typed = TYPED_MESSAGE.exec(message)
  const frames: ExceptionFrame[] = []
  if (event.filename) {
    frames.push({
      file: event.filename,
      ...(event.lineno ? { line: event.lineno } : {}),
      ...(event.colno ? { column: event.colno } : {}),
    })
  }
  return {
    type: typed ? typed[1]! : NON_ERROR_TYPE,
    message: clip(typed ? typed[2]! : message, MAX_EXCEPTION_MESSAGE_LENGTH),
    frames,
    handled: false,
    mechanism: 'onerror',
  }
}

/**
 * Écoute les exceptions non gérées et les promesses rejetées sans `catch` de la fenêtre ; renvoie
 * l'arrêt de l'écoute. Les erreurs de chargement d'une ressource (image, script) ne remontent pas
 * jusqu'à la fenêtre : seules les erreurs de script arrivent ici.
 */
export function watchExceptions(
  win: Window,
  onException: (exception: ParsedException) => void,
): () => void {
  const onError = (event: Event) => {
    try {
      if (!('message' in event)) return
      const errorEvent = event as ErrorEvent
      onException(
        errorEvent.error === undefined || errorEvent.error === null
          ? parseErrorEvent(errorEvent)
          : parseThrown(errorEvent.error, { handled: false, mechanism: 'onerror' }),
      )
    } catch {
      // La capture ne doit jamais casser la page.
    }
  }
  const onRejection = (event: Event) => {
    try {
      const { reason } = event as PromiseRejectionEvent
      onException(parseThrown(reason, { handled: false, mechanism: 'unhandledrejection' }))
    } catch {
      // La capture ne doit jamais casser la page.
    }
  }
  win.addEventListener('error', onError)
  win.addEventListener('unhandledrejection', onRejection)
  return () => {
    win.removeEventListener('error', onError)
    win.removeEventListener('unhandledrejection', onRejection)
  }
}
